import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Edit, Trash2, Sparkles, ChevronDown, ChevronUp, Repeat } from 'lucide-react';
import Layout from '../components/Layout';
import recurringService from '../services/recurringService';
import transactionService from '../services/transactionService';
import RecurringForm from '../components/RecurringForm';
import TransactionForm from '../components/TransactionForm';

export default function RecurringPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = React.useState<any | null>(null);
  const [showForm, setShowForm] = React.useState(false);
  const [editingTransaction, setEditingTransaction] = React.useState<any | null>(null);
  const [showTransactionForm, setShowTransactionForm] = React.useState(false);
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const [generatingId, setGeneratingId] = React.useState<string | null>(null);
  const [showInactive, setShowInactive] = React.useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['recurring'],
    queryFn: () => recurringService.list(),
  });

  const { data: transactionsData = [] } = useQuery({
    queryKey: ['transactions'],
    queryFn: () => transactionService.list(),
  });

  const filteredRecurring = React.useMemo(() => {
    if (!data) return [];
    return data.filter((r: any) => {
      const isActive = r.active !== false; // default to true if not set
      if (!showInactive && !isActive) return false;
      return true;
    });
  }, [data, showInactive]);

  const createMutation = useMutation({
    mutationFn: (payload: any) => recurringService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring'] });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: any) => recurringService.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring'] });
      setEditing(null);
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => recurringService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recurring'] }),
  });

  const updateTransactionMutation = useMutation({
    mutationFn: ({ id, payload }: any) => transactionService.update(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['transactions'] }),
  });

  const deleteTransactionMutation = useMutation({
    mutationFn: (id: string) => transactionService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['transactions'] }),
  });

  const onSubmit = async (payload: any) => {
    if (editing) {
      await updateMutation.mutateAsync({ id: editing.id, payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
  };

  const onSubmitTransaction = async (payload: any) => {
    if (!editingTransaction) return;
    try {
      await updateTransactionMutation.mutateAsync({ id: editingTransaction.id, payload });
      window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'success', message: 'Lançamento atualizado com sucesso.' } }));
      setShowTransactionForm(false);
      setEditingTransaction(null);
    } catch (err: any) {
      window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'error', message: err?.message || 'Falha ao atualizar lançamento' } }));
    }
  };

  const formatCurrency = (value: number) => new Intl.NumberFormat(navigator.language, { style: 'currency', currency: 'BRL' }).format(value || 0);

  const getTransactionsForRecurring = (recurringId: string) => {
    return transactionsData.filter((t: any) => t.recurringExpenseId === recurringId).sort((a: any, b: any) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
  };

  return (
    <Layout title="Contas Recorrentes" icon={Repeat}>
      <div className="flex flex-col h-full bg-gray-100">
        <div className="hidden md:block bg-white border-b border-gray-200 px-4 md:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center">
                <Repeat className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Contas Recorrentes</h1>
                <p className="text-gray-600 text-sm mt-1">Gerencie contas fixas e variáveis com lançamentos manuais por ano.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white border-b border-gray-200 px-4 md:px-8 py-2 md:py-4 flex-shrink-0">
          <div className="flex justify-start">
            <button
              onClick={() => {
                setEditing(null);
                setShowForm(true);
              }}
              className="bg-gray-900 text-white px-4 py-2 rounded-md hover:bg-gray-800 font-medium w-full md:w-auto text-sm"
            >
              + Nova Conta Recorrente
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 md:px-8 py-4 md:py-6">
          {showForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <div className="bg-white w-full max-w-xl rounded-lg shadow-lg border border-gray-200">
                <div className="flex items-center justify-between px-6 py-4 border-b">
                  <h3 className="text-lg font-semibold">{editing ? 'Editar Conta Recorrente' : 'Nova Conta Recorrente'}</h3>
                  <button
                    onClick={() => { setShowForm(false); setEditing(null); }}
                    className="text-gray-500 hover:text-gray-700"
                    aria-label="Fechar"
                  >
                    ✕
                  </button>
                </div>
                <div className="p-6">
                  <RecurringForm
                    initial={editing ?? undefined}
                    onCancel={() => { setShowForm(false); setEditing(null); }}
                    onSubmit={onSubmit}
                  />
                </div>
              </div>
            </div>
          )}

          {showTransactionForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <div className="bg-white w-full max-w-md rounded-lg shadow-lg border border-gray-200">
                <div className="flex items-center justify-between px-6 py-4 border-b">
                  <h3 className="text-lg font-semibold">Editar Lançamento</h3>
                  <button
                    onClick={() => { setShowTransactionForm(false); setEditingTransaction(null); }}
                    className="text-gray-500 hover:text-gray-700"
                    aria-label="Fechar"
                  >
                    ✕
                  </button>
                </div>
                <div className="p-6">
                  <TransactionForm
                    initial={editingTransaction ?? undefined}
                    onCancel={() => { setShowTransactionForm(false); setEditingTransaction(null); }}
                    onSubmit={onSubmitTransaction}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow border border-gray-100 p-4 md:p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="text-xl font-semibold text-gray-900">
                {showInactive ? 'Todas as Contas' : 'Contas Ativas'}
              </h2>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-gray-900"
                />
                <span className="text-sm font-medium text-gray-700">Exibir contas inativas</span>
              </label>
            </div>
            {isLoading ? (
              <div className="text-center py-8 text-gray-500">Carregando...</div>
            ) : filteredRecurring?.length ? (
              <div className="space-y-3">
                {filteredRecurring.map((r: any) => {
                  const launches = getTransactionsForRecurring(r.id);
                  const now = new Date();
                  const currentMonth = now.getMonth();
                  const currentYear = now.getFullYear();
                  const upcoming = launches
                    .filter((t: any) => {
                      if (t.isPaid) return false;
                      const d = new Date(t.dueDate);
                      const diffMonths = (d.getFullYear() - currentYear) * 12 + (d.getMonth() - currentMonth);
                      return diffMonths >= 0 && diffMonths <= 2;
                    })
                    .slice(0, 3);
                  const isExpanded = expandedId === r.id;
                  return (
                    <div key={r.id} className="border border-gray-200 rounded-lg hover:shadow-sm transition">
                      <div className="p-3 md:p-4 flex flex-col gap-3 md:gap-0 md:flex-row md:items-center">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h3 className="font-semibold text-gray-900 text-sm md:text-base">{r.name}</h3>
                            <span className="px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap">{r.category}</span>
                            <span className="px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap">{r.type === 'fixed' ? 'Fixa' : 'Variável'}</span>
                            {r.active === false && (
                              <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded whitespace-nowrap font-medium">Inativa</span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-xs md:text-sm flex-wrap">
                            {r.type === 'fixed' && r.amount ? (
                              <span className="font-bold text-red-600">{formatCurrency(r.amount)}</span>
                            ) : null}
                            <span className="text-gray-600">Vencimento: Dia {r.dayOfMonth}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-wrap order-3 md:order-3">
                          <button
                            disabled={generatingId === r.id}
                            onClick={async () => {
                              setGeneratingId(r.id);
                              try {
                                const res = await recurringService.generateLaunches(r.id);
                                window.dispatchEvent(
                                  new CustomEvent('toast', {
                                    detail: { type: 'success', message: `Gerado(s) ${res?.created ?? 0} lançamento(s) nos próximos 12 meses.` },
                                  })
                                );
                              } catch (err: any) {
                                window.dispatchEvent(
                                  new CustomEvent('toast', {
                                    detail: { type: 'error', message: `Falha ao gerar lançamentos: ${err?.message || 'erro desconhecido'}` },
                                  })
                                );
                              } finally {
                                setGeneratingId(null);
                                qc.invalidateQueries({ queryKey: ['transactions'] });
                              }
                            }}
                            className={`px-3 py-2 rounded border border-gray-300 bg-gray-100 text-gray-800 text-xs md:text-sm flex items-center gap-1 md:gap-2 flex-shrink-0 ${
                              generatingId === r.id ? 'opacity-70 cursor-not-allowed' : 'hover:bg-gray-200'
                            }`}
                            title="Gerar Lançamentos"
                          >
                            {generatingId === r.id ? (
                              <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" aria-hidden />
                            ) : (
                              <Sparkles className="w-3 h-3 md:w-4 md:h-4" />
                            )}
                            <span>{generatingId === r.id ? 'Gerando...' : 'Gerar Lançamentos'}</span>
                          </button>
                          <button
                            onClick={() => { setEditing(r); setShowForm(true); }}
                            className="p-2 rounded hover:bg-gray-100 text-blue-600 flex-shrink-0"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => { if (confirm('Tem certeza que deseja excluir?')) deleteMutation.mutate(r.id); }}
                            className="p-2 rounded hover:bg-gray-100 text-red-600 flex-shrink-0"
                            title="Excluir"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setExpandedId(isExpanded ? null : r.id)}
                            className="p-2 rounded hover:bg-gray-100 text-gray-700 flex-shrink-0"
                            title={isExpanded ? 'Recolher' : 'Expandir'}
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      {isExpanded && upcoming.length > 0 && (
                        <div className="border-t border-gray-200 bg-gray-50 p-4">
                          <div className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                            📅 Lançamentos Futuros
                          </div>
                          <div className="space-y-2">
                            {upcoming.map((t: any) => (
                              <div key={t.id} className="flex items-center justify-between rounded-md px-3 py-2 border border-gray-200 bg-white text-sm">
                                <div>
                                  <div className="font-semibold">{formatCurrency(t.amount)}</div>
                                  <div className="text-xs text-gray-600">{new Date(t.dueDate).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })} • {r.name}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs px-2 py-1 rounded-full ${t.isPaid ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`}>
                                    {t.isPaid ? '✓ Pago' : 'Pendente'}
                                  </span>
                                  <button
                                    className="p-1 rounded hover:bg-gray-200 text-blue-600"
                                    title="Editar"
                                    onClick={() => { setEditingTransaction(t); setShowTransactionForm(true); }}
                                  >
                                    <Edit className="w-3 h-3" />
                                  </button>
                                  <button
                                    className="p-1 rounded hover:bg-gray-200 text-red-600"
                                    title="Excluir"
                                    onClick={async () => {
                                      if (!confirm('Excluir este lançamento?')) return;
                                      try {
                                        await deleteTransactionMutation.mutateAsync(t.id);
                                        window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'success', message: 'Lançamento excluído.' } }));
                                      } catch (err: any) {
                                        window.dispatchEvent(new CustomEvent('toast', { detail: { type: 'error', message: err?.message || 'Falha ao excluir lançamento' } }));
                                      }
                                    }}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-10 text-gray-500">Nenhuma conta recorrente cadastrada</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
